// 職責：權限目錄 —— shop-manager / ops-console / 門店 webapi 三方共用的**唯一來源**

/*
 * 為什麼目錄放在 shop-manager 而不是 ops-console：
 *   權限的形狀就是 UI 的形狀（哪個模組、哪個分頁、哪個按鈕）。ops-console 只需要知道
 *   「有什麼可以勾選」，所以它讀取本檔匯出的 JSON（`serialize.ts` → vite build 時寫入
 *   `../ops-console/control-plane/shopmgr-permissions.json`）；webapi 的 attribute 直接寫店碼字串，
 *   `tests/unit/webapi-codes.spec.ts` 逐一對照本目錄。三方 drift 一律由測試捕捉，不靠人手記憶。
 *
 * Key 文法：`<module>[.<section>]:<action>`
 *   · 每段 `[a-z][a-z0-9-]*`；只寫 `menu` / `settings.dict` ⇒ 正規化為 `:view`
 *   · 通配 `*`、`<m>:*`、`<m>.<s>:*` —— 只在 ops 範本／授權出現，永不寫入店庫
 *
 * 隱含（`implies`；`expand()` 會補齊閉包）：
 *   ① `<m>.<s>:<a>` ⇒ `<m>.<s>:view` ⇒ `<m>:view`
 *   ② `<m>.<s>:<a>`（a ≠ view）⇒ 聚合碼 `<m>:<a>`（衍生；webapi 對 periods/combos 只能粗略判斷時就依靠它）
 *   ③ `<m>:<a>`（無分頁模組）⇒ `<m>:view`
 *   ④ `pos:*` 不隱含任何權限
 *   ★ 刻意**沒有**「`<m>:view` ⇒ 全部分頁 view」：一旦有這條，①與它串連起來就會變成
 *     「授 settings.dict:edit ⇒ settings:view ⇒ 看到全部四個分頁」。模組 view 只是「能進入模組」，
 *     分頁要逐一授予（SyncService 寫閉包時會把 `<m>:*` 展開成全部分頁列，所以「整個模組」照樣可行）。
 *
 * 店庫編碼（`codeOf`，寫入 `merchant_function.code`，varchar(45)）：
 *   `menu:view` → `shopmgr-menu`（view 省略 ⇒ 09-02 已寫入門店的 `shopmgr-menu` / `shopmgr-settings-dict` 原封不動）
 *   `menu:add` → `shopmgr-menu:add`
 *   `settings.dict:edit` → `shopmgr-settings-dict:edit`
 *   聚合 `periods:edit` → `shopmgr-periods:edit`
 *   `pos:void` → `void`（type=2，店庫本身已有那一列，不另開新列）
 *   `uri` 一律是模組路由（分頁共用），`name` 是 `新後台-模組-分頁-動作`。
 *
 * ⚠️ 本檔（與 `serialize.ts`）不可以 import 任何 `@/` runtime 模組：
 *    `scripts/export-permission-catalog.ts` 用 node 直接執行，`vite.config.ts` 由 esbuild 打包，
 *    兩者都不認識 `@/` alias。只准 type-only import，而且相對 import 必須寫上 `.ts`。
 */

/* ────────────────────────────── 目錄定義 ────────────────────────────── */

export const STANDARD_ACTIONS = {
  view: '檢視',
  add: '新增',
  edit: '編輯',
  delete: '刪除',
} as const
export type StandardAction = keyof typeof STANDARD_ACTIONS

export type NavGroup = '營運' | '洞察' | '設定'

/** 側欄圖示；與 `shared/ui/IconSprite.vue` 的 `ICON_NAMES` 對齊，routes.ts 會以型別驗證 */
export type ModuleIcon =
  | 'i-menu'
  | 'i-list'
  | 'i-clock'
  | 'i-layers'
  | 'i-box'
  | 'i-upload'
  | 'i-chart'
  | 'i-card'
  | 'i-printer'
  | 'i-users'
  | 'i-gear'

/** POS 碼分組：`checked` = scpos 確實會 check 的 11 個；`legacy` = 模板有但 scpos 不讀取，摺疊顯示 */
export type PosGroup = 'checked' | 'legacy'

/** 店庫 `merchant_function.type`：5 = 後台按鈕（本目錄新列）、2 = POS 動作（沿用舊列） */
export type StoreType = 5 | 2
export const STORE_TYPE_BUTTON: StoreType = 5
export const STORE_TYPE_POS: StoreType = 2

export const STORE_CODE_PREFIX = 'shopmgr-'
/** `merchant_function.code` / `name` 都是 varchar(45)，超長時 PostgreSQL 會報錯而不是截斷 */
export const STORE_CODE_MAX = 45
export const STORE_NAME_PREFIX = '新後台-'

export const WILDCARD_ALL = '*'

interface ActionObjDef {
  readonly key: string
  readonly label: string
  /** 只有 `pos` 模組用：對應店庫既有 type=2 碼 */
  readonly storeCode?: string
  readonly group?: PosGroup
}
type ActionDef = StandardAction | ActionObjDef

interface SectionDef {
  readonly section: string
  readonly label: string
  readonly actions: readonly ActionDef[]
}

interface ModuleDef {
  readonly module: string
  readonly label: string
  readonly icon: ModuleIcon | null
  readonly navGroup: NavGroup | null
  /** 對應路由名；null = 不是後台模組（pos） */
  readonly route: string | null
  readonly storeType: StoreType
  readonly actions: readonly ActionDef[]
  readonly sections: readonly SectionDef[]
}

const CRUD = ['view', 'add', 'edit', 'delete'] as const
const CRUD_CLONE = ['view', 'add', 'edit', 'delete', { key: 'clone', label: '複製' }] as const
const VIEW_ONLY = ['view'] as const

/** POS 動作（type=2）—— 名稱與代碼逐條抄自 `webapi/sql/store-template/food.sql`（id 84–231） */
const POS_ACTIONS = [
  // scpos 確實會 check 的 11 個（`scpos/packages/vue/src/api/webapi.ts:126-130` checkAuthFunction 的呼叫點）
  { key: 'void', label: 'VOID單', storeCode: 'void', group: 'checked' },
  { key: 'app-void', label: 'app VOID', storeCode: 'app-void', group: 'checked' },
  { key: 'hold-bill', label: 'Hold單', storeCode: 'hold-bill', group: 'checked' },
  { key: 'change-dinners', label: '改人數', storeCode: 'change-dinners', group: 'checked' },
  { key: 'change-table', label: '轉台', storeCode: 'change-table', group: 'checked' },
  { key: 'query-bill', label: 'app查單', storeCode: 'query-bill', group: 'checked' },
  { key: 'print-qrcode', label: '印搭台紙', storeCode: 'print-qrcode', group: 'checked' },
  { key: 'label-print', label: '標簽打印', storeCode: 'label-print', group: 'checked' },
  { key: 'order-dinner', label: '點餐', storeCode: 'order-dinner', group: 'checked' },
  {
    key: 'order-table-share',
    label: 'App搭檯點餐',
    storeCode: 'order-table-share',
    group: 'checked',
  },
  { key: 'print-bill', label: '打印小票', storeCode: 'print-bill', group: 'checked' },
  // 模板有、scpos 不讀取的 14 個 —— 保留是為了本地角色 round-trip 時不會靜默遺失
  { key: 'table-list', label: '台號管理', storeCode: 'table-list', group: 'legacy' },
  { key: 'bill-list', label: '賬單管理', storeCode: 'bill-list', group: 'legacy' },
  { key: 'report', label: '報表', storeCode: 'report', group: 'legacy' },
  { key: 'balance-data', label: '清機', storeCode: 'balance-data', group: 'legacy' },
  { key: 'stock', label: '庫存設置', storeCode: 'stock', group: 'legacy' },
  { key: 'find-bill', label: '搜索賬單', storeCode: 'find-bill', group: 'legacy' },
  { key: 'create-bill', label: '開台', storeCode: 'create-bill', group: 'legacy' },
  { key: 'paid-bill', label: '埋单功能', storeCode: 'paid-bill', group: 'legacy' },
  { key: 'swipe-card', label: '拍卡管理', storeCode: 'swipe-card', group: 'legacy' },
  { key: 'add-goods', label: '加單', storeCode: 'add-goods', group: 'legacy' },
  { key: 'merge-bill', label: '合單', storeCode: 'merge-bill', group: 'legacy' },
  { key: 'open-drawer', label: '開櫃', storeCode: 'open-drawer', group: 'legacy' },
  { key: 'his-bill', label: '歷史賬單', storeCode: 'his-bill', group: 'legacy' },
  { key: 'quick-bread', label: '麵包點餐', storeCode: 'quick-bread', group: 'legacy' },
] as const

/**
 * ★ 目錄本體。每個 add/edit/delete 都對應一個真實 mutation（見 doc authz-v2 §3.2）。
 *   加 key 之前先問：webapi 有沒有端點可以阻擋？阻擋不了的 key 只是裝飾（`menu:clone`、`reports:export` 就是這樣被剔除的）。
 */
const DEF = [
  {
    module: 'menu',
    label: '菜單管理',
    icon: 'i-menu',
    navGroup: '營運',
    route: 'menu',
    storeType: 5,
    actions: [
      ...CRUD,
      { key: 'photo', label: '相片' },
      { key: 'structure', label: '改碼／套餐結構' },
    ],
    sections: [],
  },
  {
    module: 'periods',
    label: '供應時段',
    icon: 'i-clock',
    navGroup: '營運',
    route: 'periods',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      { section: 'dinein', label: '堂食', actions: CRUD },
      { section: 'takeaway', label: '外賣自取', actions: CRUD },
      { section: 'selfservice', label: '自助售賣', actions: CRUD },
      { section: 'quickorder', label: '快速落單', actions: CRUD },
      // 撇數面板的「補回一件」要 `deptsplit:add`（2026-09-05：改餐牌不准碰撇數）；
      // 編輯部門餐品仍在 menu 抽屜（DeptSplitDefaultEditor → menu:edit）
      { section: 'deptsplit', label: '撇數', actions: ['view', 'add'] },
    ],
  },
  {
    module: 'combos',
    label: '套餐搭配',
    icon: 'i-layers',
    navGroup: '營運',
    route: 'combos',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      { section: 'combo', label: '跟餐', actions: CRUD_CLONE },
      { section: 'addon', label: '撞餐·單品', actions: CRUD_CLONE },
      { section: 'drink', label: '撞餐·飲品', actions: CRUD_CLONE },
      { section: 'flavour', label: '共用改碼包', actions: CRUD_CLONE },
    ],
  },
  {
    // M12 零售商品管理（2026-09-12 由借用 menu 權限改為獨立模組）；側欄只限零售店見到，
    // 那是門店業務屬性，由 routes.ts 的 RETAIL_ONLY_MODULES 決定，不寫進目錄
    module: 'retail-goods',
    label: '商品管理',
    icon: 'i-box',
    navGroup: '營運',
    route: 'retail-goods',
    storeType: 5,
    actions: CRUD,
    sections: [],
  },
  {
    module: 'goods-import',
    label: '商品匯入',
    icon: 'i-upload',
    navGroup: '營運',
    route: 'goods-import',
    storeType: 5,
    actions: ['view', { key: 'apply', label: '正式匯入' }],
    sections: [],
  },
  {
    /*
     * 已清機訂單的補撇／重撇／撤銷（2026-10-06）。清機後 POS 不能再撇數，原本只可由技術人員改資料庫；
     * `edit` 是唯一的寫入動作，三種操作共用（webapi `api/dept-split-his/split|undo` 認 `shopmgr-deptsplit-his:edit`）。
     * 預設不授予任何門店角色 —— 修改已清機訂單會改動已出過的部門報表，要在 ops-console 逐個開放。
     */
    module: 'deptsplit-his',
    label: '已清機補撇數',
    icon: 'i-list',
    navGroup: '營運',
    route: 'deptsplit-his',
    storeType: 5,
    actions: ['view', 'edit'],
    sections: [],
  },
  {
    module: 'reports',
    label: '報表分析',
    icon: 'i-chart',
    navGroup: '洞察',
    route: 'reports',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      { section: 'goods', label: '餐品銷售', actions: VIEW_ONLY },
      { section: 'payment', label: '付款方式', actions: VIEW_ONLY },
      { section: 'paydetail', label: '付款詳列', actions: VIEW_ONLY },
      { section: 'items', label: '訂單分析', actions: VIEW_ONLY },
      { section: 'daily', label: '每日營運報表', actions: VIEW_ONLY },
    ],
  },
  {
    module: 'payments',
    label: '收款與優惠',
    icon: 'i-card',
    navGroup: '設定',
    route: 'payments',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      { section: 'methods', label: '付款方式', actions: CRUD },
      { section: 'discount', label: '折扣', actions: CRUD },
      { section: 'promo', label: '推廣優惠', actions: CRUD },
    ],
  },
  {
    module: 'kitchen',
    label: '廚房與打印',
    icon: 'i-printer',
    navGroup: '設定',
    route: 'kitchen',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      { section: 'printers', label: '打印機', actions: CRUD },
      { section: 'depts', label: '出品部門', actions: CRUD },
      { section: 'tables', label: '餐桌', actions: CRUD },
      { section: 'pickup', label: '取餐叫號組', actions: CRUD },
    ],
  },
  {
    module: 'employees',
    label: '員工與權限',
    icon: 'i-users',
    navGroup: '設定',
    route: 'employees',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      {
        section: 'staff',
        label: '員工帳號',
        actions: [...CRUD, { key: 'reset-password', label: '重設密碼' }],
      },
      { section: 'roles', label: '角色與權限', actions: CRUD },
      { section: 'access', label: 'ops 帳號存取', actions: VIEW_ONLY },
      { section: 'audit', label: '權限對照', actions: VIEW_ONLY },
    ],
  },
  {
    module: 'settings',
    label: '系統設定',
    icon: 'i-gear',
    navGroup: '設定',
    route: 'settings',
    storeType: 5,
    actions: VIEW_ONLY,
    sections: [
      { section: 'dict', label: '改碼造法', actions: CRUD },
      { section: 'shopinfo', label: '門店資料', actions: ['view', 'edit'] },
      { section: 'jobs', label: '定時任務', actions: CRUD },
      { section: 'yeahpay', label: 'YeahPay 收單', actions: CRUD },
    ],
  },
  {
    module: 'pos',
    label: 'POS 收銀機',
    icon: null,
    navGroup: null,
    route: null,
    storeType: 2,
    actions: POS_ACTIONS,
    sections: [],
  },
] as const satisfies readonly ModuleDef[]

/* ────────────────────────────── 由定義推導的字面型別 ────────────────────────────── */

type Def = (typeof DEF)[number]
type ActionKey<A> = A extends string
  ? A
  : A extends { readonly key: infer K extends string }
    ? K
    : never
type ModuleActionKeys<M> = M extends {
  readonly module: infer Mod extends string
  readonly actions: readonly (infer A)[]
}
  ? `${Mod}:${ActionKey<A>}`
  : never
type SectionActionKeys<M> = M extends {
  readonly module: infer Mod extends string
  readonly sections: readonly (infer S)[]
}
  ? S extends { readonly section: infer Sec extends string; readonly actions: readonly (infer A)[] }
    ? `${Mod}.${Sec}:${ActionKey<A>}`
    : never
  : never
/** 聚合碼：有分頁的模組，每個出現過的非 view 動作都有一個 `<m>:<a>`（衍生，不可以直接授予） */
type AggregateKeys<M> = M extends {
  readonly module: infer Mod extends string
  readonly sections: readonly (infer S)[]
}
  ? S extends { readonly actions: readonly (infer A)[] }
    ? `${Mod}:${Exclude<ActionKey<A>, 'view'>}`
    : never
  : never

export type ModuleKey = Def['module']
/** 全部葉 key 的字面聯集 —— `can('menu:ad')` 是 vue-tsc 錯誤，不是執行期靜默 false */
export type PermissionKey = ModuleActionKeys<Def> | SectionActionKeys<Def> | AggregateKeys<Def>
export type WildcardKey = typeof WILDCARD_ALL | `${ModuleKey}:*` | `${ModuleKey}.${string}:*`

/* ────────────────────────────── 執行期結構 ────────────────────────────── */

export interface CatalogAction {
  readonly key: PermissionKey
  readonly action: string
  readonly label: string
  readonly code: string
  readonly name: string
  readonly storeCode: string | null
  readonly group: PosGroup | null
  /** true = 聚合碼（由分頁動作推導；UI 樹不顯示、範本不可以直接授予） */
  readonly derived: boolean
}

export interface CatalogSection {
  readonly section: string
  readonly label: string
  readonly viewKey: PermissionKey
  readonly actions: readonly CatalogAction[]
}

export interface CatalogModule {
  readonly module: ModuleKey
  readonly label: string
  readonly icon: ModuleIcon | null
  readonly navGroup: NavGroup | null
  readonly route: string | null
  readonly storeType: StoreType
  readonly hasView: boolean
  readonly actions: readonly CatalogAction[]
  readonly sections: readonly CatalogSection[]
  readonly aggregates: readonly CatalogAction[]
}

export interface KeyInfo {
  readonly key: PermissionKey
  readonly module: ModuleKey
  readonly section: string | null
  readonly action: string
  readonly label: string
  readonly code: string
  readonly name: string
  readonly storeCode: string | null
  readonly storeType: StoreType
  readonly derived: boolean
  readonly group: PosGroup | null
}

const SEGMENT = /^[a-z][a-z0-9-]*$/

function actionKeyOf(a: ActionDef): string {
  return typeof a === 'string' ? a : a.key
}
function actionLabelOf(a: ActionDef): string {
  return typeof a === 'string' ? STANDARD_ACTIONS[a] : a.label
}

function buildKey(module: string, section: string | null, action: string): string {
  return section === null ? `${module}:${action}` : `${module}.${section}:${action}`
}

function buildCode(module: string, section: string | null, action: string): string {
  const base =
    section === null ? `${STORE_CODE_PREFIX}${module}` : `${STORE_CODE_PREFIX}${module}-${section}`
  return action === 'view' ? base : `${base}:${action}`
}

function buildName(
  moduleLabel: string,
  sectionLabel: string | null,
  actionLabel: string,
  action: string,
  derived: boolean,
): string {
  const parts = [moduleLabel]
  if (sectionLabel !== null) parts.push(sectionLabel)
  if (action !== 'view') parts.push(derived ? `${actionLabel}(任一分頁)` : actionLabel)
  return `${STORE_NAME_PREFIX}${parts.join('-')}`
}

interface Built {
  modules: CatalogModule[]
  info: Map<string, KeyInfo>
  codeToKey: Map<string, PermissionKey>
  posCodeToKey: Map<string, PermissionKey>
}

function build(): Built {
  const modules: CatalogModule[] = []
  const info = new Map<string, KeyInfo>()
  const codeToKey = new Map<string, PermissionKey>()
  const posCodeToKey = new Map<string, PermissionKey>()

  const register = (
    m: ModuleDef,
    section: SectionDef | null,
    a: ActionDef,
    derived: boolean,
  ): CatalogAction => {
    const action = actionKeyOf(a)
    const label = actionLabelOf(a)
    const key = buildKey(m.module, section?.section ?? null, action) as PermissionKey
    const storeCode = typeof a === 'string' ? null : (a.storeCode ?? null)
    const code = storeCode ?? buildCode(m.module, section?.section ?? null, action)
    const name = buildName(m.label, section?.label ?? null, label, action, derived)
    const group = typeof a === 'string' ? null : (a.group ?? null)
    const entry: CatalogAction = { key, action, label, code, name, storeCode, group, derived }
    info.set(key, {
      key,
      module: m.module as ModuleKey,
      section: section?.section ?? null,
      action,
      label,
      code,
      name,
      storeCode,
      storeType: m.storeType,
      derived,
      group,
    })
    if (m.storeType === STORE_TYPE_POS) posCodeToKey.set(code, key)
    else codeToKey.set(code, key)
    return entry
  }

  for (const m of DEF as readonly ModuleDef[]) {
    const actions = m.actions.map((a) => register(m, null, a, false))
    const sections: CatalogSection[] = m.sections.map((s) => ({
      section: s.section,
      label: s.label,
      viewKey: buildKey(m.module, s.section, 'view') as PermissionKey,
      actions: s.actions.map((a) => register(m, s, a, false)),
    }))
    // 聚合碼：分頁出現過的非 view 動作，每個一條（按首次出現次序）
    const seen = new Map<string, ActionDef>()
    for (const s of m.sections) {
      for (const a of s.actions) {
        const k = actionKeyOf(a)
        if (k !== 'view' && !seen.has(k)) seen.set(k, a)
      }
    }
    const aggregates = [...seen.values()].map((a) => register(m, null, a, true))
    modules.push({
      module: m.module as ModuleKey,
      label: m.label,
      icon: m.icon,
      navGroup: m.navGroup,
      route: m.route,
      storeType: m.storeType,
      hasView: m.actions.some((a) => actionKeyOf(a) === 'view'),
      actions,
      sections,
      aggregates,
    })
  }
  return { modules, info, codeToKey, posCodeToKey }
}

const BUILT = build()

export const CATALOG: readonly CatalogModule[] = BUILT.modules
/** 全部葉 key（含聚合碼），目錄次序 */
export const ALL_KEYS: ReadonlySet<PermissionKey> = new Set(
  BUILT.info.keys() as Iterable<PermissionKey>,
)
const KEY_INFO: ReadonlyMap<string, KeyInfo> = BUILT.info
const CODE_TO_KEY: ReadonlyMap<string, PermissionKey> = BUILT.codeToKey
const POS_CODE_TO_KEY: ReadonlyMap<string, PermissionKey> = BUILT.posCodeToKey
const MODULE_BY_KEY: ReadonlyMap<string, CatalogModule> = new Map(CATALOG.map((m) => [m.module, m]))

/* ────────────────────────────── 查詢 ────────────────────────────── */

export function isKnown(key: string): key is PermissionKey {
  return KEY_INFO.has(key)
}

export function keyInfo(key: PermissionKey): KeyInfo {
  const found = KEY_INFO.get(key)
  if (found === undefined) throw new Error(`未知權限 key：${key}`)
  return found
}

export function isModuleKey(value: string): value is ModuleKey {
  return MODULE_BY_KEY.has(value)
}

export function moduleOf(module: ModuleKey): CatalogModule {
  const found = MODULE_BY_KEY.get(module)
  if (found === undefined) throw new Error(`未知模組：${module}`)
  return found
}

export function sectionsOf(module: ModuleKey): readonly CatalogSection[] {
  return moduleOf(module).sections
}

/** 把任何字串拆成三段；未知 / 通配 → null（不正規化 view：由呼叫端決定是否需要） */
export function parseKey(
  key: string,
): { module: ModuleKey; section: string | null; action: string } | null {
  const found = KEY_INFO.get(key)
  if (found !== undefined)
    return { module: found.module, section: found.section, action: found.action }
  return null
}

/** 只寫 `menu` / `settings.dict` ⇒ 補上 `:view`；已經有 `:` 就保持原樣 */
export function normalizeKey(key: string): string {
  const trimmed = key.trim()
  if (trimmed === WILDCARD_ALL || trimmed.includes(':')) return trimmed
  return `${trimmed}:view`
}

export function keyOf(module: ModuleKey, section: string | null, action: string): PermissionKey {
  const key = buildKey(module, section, action)
  if (!isKnown(key)) throw new Error(`未知權限 key：${key}`)
  return key
}

export function moduleViewKey(module: ModuleKey): PermissionKey {
  return keyOf(module, null, 'view')
}

export function sectionViewKey(module: ModuleKey, section: string): PermissionKey {
  return keyOf(module, section, 'view')
}

/** 聚合碼 `<m>:<a>`；該模組沒有分頁或沒有這個動作 → null */
export function aggregateKey(module: ModuleKey, action: string): PermissionKey | null {
  const key = buildKey(module, null, action)
  const found = KEY_INFO.get(key)
  return found !== undefined && found.derived ? found.key : null
}

export function codeOf(key: PermissionKey): string {
  return keyInfo(key).code
}

export function nameOf(key: PermissionKey): string {
  return keyInfo(key).name
}

/**
 * 店碼 → key。type=5 只認 `shopmgr-*`；type=2 只認 POS 目錄中有的碼；其餘 null。
 * ★ 用反查表而不是拆字串：模組名本身有 `-`（goods-import），拆 `-` 一定會拆錯。
 */
export function parseCode(code: string, type: number | null): PermissionKey | null {
  const trimmed = code.trim()
  if (type === STORE_TYPE_BUTTON) return CODE_TO_KEY.get(trimmed) ?? null
  if (type === STORE_TYPE_POS) return POS_CODE_TO_KEY.get(trimmed) ?? null
  return null
}

export function labelOf(key: PermissionKey): string {
  const k = keyInfo(key)
  const m = moduleOf(k.module)
  const parts = [m.label]
  if (k.section !== null) {
    parts.push(m.sections.find((s) => s.section === k.section)?.label ?? k.section)
  }
  parts.push(k.derived ? `${k.label}（任一分頁）` : k.label)
  return parts.join(' › ')
}

/* ────────────────────────────── 隱含 / 展開 ────────────────────────────── */

/** 自己 + 隱含的 key（見檔頭四條規則）；次序：自己在前 */
export function implies(key: PermissionKey): readonly PermissionKey[] {
  const k = keyInfo(key)
  const out: PermissionKey[] = [key]
  if (k.storeType === STORE_TYPE_POS) return out
  const m = moduleOf(k.module)
  if (k.section !== null) {
    out.push(sectionViewKey(k.module, k.section))
    if (k.action !== 'view') {
      const agg = aggregateKey(k.module, k.action)
      if (agg !== null) out.push(agg)
    }
  }
  if (m.hasView && k.action !== 'view') out.push(moduleViewKey(k.module))
  if (k.section !== null && m.hasView) out.push(moduleViewKey(k.module))
  return [...new Set(out)]
}

export function isWildcard(key: string): boolean {
  return key === WILDCARD_ALL || key.endsWith(':*')
}

/** 通配 → 葉 key 清單；不是通配或者對應不上 → null */
function expandWildcard(key: string): PermissionKey[] | null {
  if (key === WILDCARD_ALL) return [...ALL_KEYS]
  if (!key.endsWith(':*')) return null
  const scope = key.slice(0, -2)
  const dot = scope.indexOf('.')
  const moduleName = dot === -1 ? scope : scope.slice(0, dot)
  const sectionName = dot === -1 ? null : scope.slice(dot + 1)
  const m = MODULE_BY_KEY.get(moduleName)
  if (m === undefined) return null
  if (sectionName === null) {
    return [
      ...m.actions.map((a) => a.key),
      ...m.sections.flatMap((s) => s.actions.map((a) => a.key)),
      ...m.aggregates.map((a) => a.key),
    ]
  }
  const s = m.sections.find((x) => x.section === sectionName)
  return s === undefined ? null : s.actions.map((a) => a.key)
}

export interface ExpandResult {
  granted: Set<PermissionKey>
  unknown: string[]
}

/**
 * 通配展開 + 閉包。輸入可以是範本 key、授權 key 或者店碼反查回來的 key；
 * 未知的不會靜默遺失，收集在 `unknown` 供 audit 頁顯示（目錄版本錯配即時現形）。
 */
export function expand(keys: Iterable<string>): ExpandResult {
  const granted = new Set<PermissionKey>()
  const unknown: string[] = []
  for (const raw of keys) {
    const key = normalizeKey(raw)
    const leaves = expandWildcard(key)
    if (leaves !== null) {
      for (const leaf of leaves) for (const k of implies(leaf)) granted.add(k)
      continue
    }
    if (!isKnown(key)) {
      unknown.push(raw)
      continue
    }
    for (const k of implies(key)) granted.add(k)
  }
  return { granted, unknown }
}

/* ────────────────────────────── 自我驗證 ────────────────────────────── */

/** 目錄結構問題清單；空 = 正常。`catalog.spec.ts` 與 control-plane 啟動時都會執行同一套規則 */
export function validateCatalog(): string[] {
  const problems: string[] = []
  const codes = new Map<string, string>()
  for (const [key, k] of KEY_INFO) {
    const parsed = parseKeyString(key)
    if (parsed === null) problems.push(`key 文法錯：${key}`)
    if (k.code.length > STORE_CODE_MAX) problems.push(`code 超過 ${STORE_CODE_MAX} 字：${k.code}`)
    if (k.name.length > STORE_CODE_MAX) problems.push(`name 超過 ${STORE_CODE_MAX} 字：${k.name}`)
    const dup = codes.get(k.code)
    if (dup !== undefined) problems.push(`code 重複：${k.code}（${dup} / ${key}）`)
    codes.set(k.code, key)
    if (k.storeType === STORE_TYPE_POS) {
      if (k.storeCode === null) problems.push(`pos key 沒有 storeCode：${key}`)
      if (k.action === 'view') problems.push(`pos 不應該有 view：${key}`)
    } else if (k.storeCode !== null) {
      problems.push(`只有 pos 可以有 storeCode：${key}`)
    } else if (!k.code.startsWith(STORE_CODE_PREFIX)) {
      problems.push(`店碼要以 ${STORE_CODE_PREFIX} 開頭：${k.code}`)
    }
  }
  for (const m of CATALOG) {
    if (m.sections.length > 0 && !m.hasView) problems.push(`有分頁的模組必須有 view：${m.module}`)
    for (const s of m.sections) {
      if (!s.actions.some((a) => a.action === 'view'))
        problems.push(`分頁要有 view：${m.module}.${s.section}`)
    }
    if (m.route !== null && m.route !== m.module)
      problems.push(`route 與 module 必須一致：${m.module}`)
  }
  return problems
}

function parseKeyString(
  key: string,
): { module: string; section: string | null; action: string } | null {
  const colon = key.indexOf(':')
  if (colon === -1) return null
  const scope = key.slice(0, colon)
  const action = key.slice(colon + 1)
  const dot = scope.indexOf('.')
  const module = dot === -1 ? scope : scope.slice(0, dot)
  const section = dot === -1 ? null : scope.slice(dot + 1)
  if (!SEGMENT.test(module) || !SEGMENT.test(action)) return null
  if (section !== null && !SEGMENT.test(section)) return null
  return { module, section, action }
}
